import axios from "axios";
import { openBillingLimitModal } from "@/utils/billingModalController";
import { client } from "@/generated/client.gen";
import { client as licenseClient } from "@/licensegenerated/client.gen";
import { softNavigateTo } from "@/utils/generic";
import { setActiveGroupId } from "@/utils/activeGroup";
import { clearCache } from "@/utils/queryCache";
import { SpotToast } from "@/components/SpotToast";
import { saveReturnTo } from "@/components/ReturnToHandler";
import {
  isOutOfStorageError,
  openStorageLimitDialog,
} from "@/utils/storageLimitController";

// Preserve the deep link the user was on before a 401-driven logout
// redirect strips it. /auth/logout → /welcome has no query string by the
// time it lands, so without this the post-login ReturnToHandler has
// nothing to consume and the user ends up at /.
const stashCurrentPathForReturn = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  const { pathname, search, hash } = window.location;
  if (pathname === "/welcome" || pathname.startsWith("/auth/")) {
    return;
  }
  saveReturnTo(`${pathname}${search}${hash}`);
};

/**
 * Errors handled by the global 403 interceptor are tagged so that local
 * mutation onError callbacks can skip their generic fallback toast and
 * avoid double-notifying the user.
 */

client.setConfig({
  baseURL: "/api/backend",
  throwOnError: true,
  paramsSerializer: {
    indexes: null,
  },
});

licenseClient.setConfig({
  baseURL: "/api/license",
  throwOnError: true,
  paramsSerializer: {
    indexes: null,
  },
});

const IGNORE_401_URLS = [
  "/api/backend/authorization-context",
  "/api/backend/tenant/feature-flags",
  "/api/backend/billing",
  "/api/backend/tenant/me",
  "/api/backend/billing/users/me",
  "/api/backend/billing/groups/me",
  "/billing",
];

// By path: the authorization context call carries its workspace as a query string.
const isIgnored401 = (url: string | undefined) =>
  !!url && IGNORE_401_URLS.includes(url.split("?")[0]);

// retrying 429 responses middleware
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryRequest = async (error: any): Promise<any> => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second base delay
  const retryCount = error.config?.__retryCount || 0;

  if (retryCount >= maxRetries) {
    return Promise.reject(error);
  }

  const retryAfter = error.response?.headers?.["retry-after"];
  const delay = retryAfter
    ? parseFloat(retryAfter) * 1000 // Convert seconds to milliseconds
    : baseDelay * Math.pow(2, retryCount); // Exponential backoff

  await sleep(delay);

  // Clone the original request config
  const config = { ...error.config, __retryCount: retryCount + 1 };

  try {
    return await client.instance.request(config);
  } catch (retryError: any) {
    return Promise.reject(retryError);
  }
};

client.instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (isIgnored401(error.response?.config?.url)) {
      return;
    }
    if (error.response?.status === 429) {
      return retryRequest(error);
    }
    /* Storage - no disk space left */
    if (isOutOfStorageError(error)) {
      openStorageLimitDialog();
    }
    /* Billing - no credits */
    if (error.response?.status === 402) {
      openBillingLimitModal(error.response?.data?.params);
    }
    if (error.response?.status === 401) {
      clearCache();
      const identifier = error.response?.data?.identifier;
      if (identifier === "disabled_account_error") {
        // Store error in sessionStorage to persist across logout redirect
        if (typeof window !== "undefined" && window.sessionStorage) {
          sessionStorage.setItem(
            "auth_error",
            "Your account has been disabled. Please contact your administrator."
          );
        }
        window.location.href = `/auth/logout?returnTo=/welcome`;
        return;
      }
      if (error.response?.config?.url?.includes("/api/public/")) {
        return;
      }
      // /changelog has a public sibling — bounce there instead of /welcome,
      // otherwise the unauth user never reaches the public page.
      if (window.location.pathname === "/changelog") {
        window.location.href = "/public/changelog";
        return;
      }
      if (!window.location.pathname.startsWith("/public/")) {
        stashCurrentPathForReturn();
        window.location.href = "/auth/logout";
      }
    }
    if (
      error.response?.status === 404 &&
      error.response?.data?.detail?.group_id
    ) {
      const groupId = error.response.data.detail.group_id;
      const currentPath = window.location.pathname;
      const requestUrl = error.response?.config?.url || "";

      const pathSegments = currentPath.split("/").filter(Boolean);
      const resourceId = pathSegments[pathSegments.length - 1];

      const isMainResourceRequest =
        resourceId && requestUrl.includes(resourceId);

      if (
        isMainResourceRequest &&
        (currentPath.includes("/assistant") ||
          currentPath.includes("/flows") ||
          currentPath.includes("/data-hub") ||
          currentPath.includes("/apps/f/") ||
          currentPath.includes("/apps/c/") ||
          currentPath.includes("/apps/d/"))
      ) {
        setActiveGroupId(groupId);
        softNavigateTo(currentPath);
        if (typeof window !== "undefined" && window.SpotToast) {
          window.SpotToast.info(
            "Switched to the workspace containing this resource."
          );
        }
      }
    }
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (isIgnored401(error.response?.config?.url)) {
      return;
    }
    if (error.response?.status === 429) {
      return retryRequest(error);
    }
    if (error.response?.status === 401) {
      const identifier = error.response?.data?.identifier;
      if (identifier === "disabled_account_error") {
        // Store error in sessionStorage to persist across logout redirect
        if (typeof window !== "undefined" && window.sessionStorage) {
          sessionStorage.setItem(
            "auth_error",
            "Your account has been disabled. Please contact your administrator."
          );
        }
        clearCache();
        window.location.href = `/auth/logout?returnTo=/welcome`;
        return;
      }
      if (error.response?.config?.url?.includes("/api/public/")) {
        return;
      }
      if (!window.location.pathname.startsWith("/public/")) {
        clearCache();
        stashCurrentPathForReturn();
        window.location.href = "/auth/logout";
      }
    }
    if (error.response?.status === 402) {
      openBillingLimitModal(error.response?.data?.params);
    }
    /* Storage - no disk space left */
    if (isOutOfStorageError(error)) {
      openStorageLimitDialog();
    }
    if (
      error.response?.status === 404 &&
      error.response?.data?.detail?.group_id
    ) {
      const groupId = error.response.data.detail.group_id;
      const currentPath = window.location.pathname;
      const requestUrl = error.response?.config?.url || "";

      const pathSegments = currentPath.split("/").filter(Boolean);
      const resourceId = pathSegments[pathSegments.length - 1];

      const isMainResourceRequest =
        resourceId && requestUrl.includes(resourceId);

      if (
        isMainResourceRequest &&
        (currentPath.includes("/assistant") ||
          currentPath.includes("/flows") ||
          currentPath.includes("/data-hub") ||
          currentPath.includes("/apps/f/") ||
          currentPath.includes("/apps/c/") ||
          currentPath.includes("/apps/d/"))
      ) {
        setActiveGroupId(groupId);
        softNavigateTo(currentPath);
        if (typeof window !== "undefined" && window.SpotToast) {
          window.SpotToast.info(
            "Switched to the workspace containing this resource."
          );
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
